import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import { useResource } from '@landsbankinn/l16n';
import {
  ButtonPlus,
  Row,
  device,
  typography,
  shouldForwardProp,
} from '@landsbankinn/ui';
import get from 'lodash.get';
import React, { useState } from 'react';
import { useSpring, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import res from './QuoteExpand.res';
import { SliceWrapper } from './SliceWrapper';
interface IProps {
  title: string | null;
  copy: React.ReactNode;
  expandableCopy: React.ReactNode;
  topSplitter: boolean;
  cards: React.ReactNode;
  onMouseEnter?: any;
  onMouseLeave?: any;
}

const Title = styled.h3`
  ${typography.h3}

  margin-bottom: 42px;
`;

const Copy = styled.div``;

const Expandable = styled.div``;

const AuthorWrapper = styled(SliceWrapper)<{ topSplitter?: boolean }>`
  ${Row} {
    position: relative;
    flex-flow: column-reverse nowrap;
    flex-direction: column-reverse;
    ${(props) =>
      props.topSplitter &&
      `    
        padding-top: 56px;
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0px;
          left: 0;
          right: 0;

          width: 100%;
          height: 1px;
          background-color: ${props.theme.colors.primary};
        }
    `}

    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: 20px;
      left: 0;
      right: 0;

      width: 100%;
      height: 1px;
      background-color: ${(props) => props.theme.colors.primary};
    }

    @media ${device.tablet} {
      flex-flow: inherit;
      &::after {
        bottom: 0;
        left: 10px;
        right: -10px;
        width: calc(100% - 20px);
      }
      ${(props) =>
        props.topSplitter &&
        `  
          &::before {
            top: 0px;
            left: 10px;
            right: -10px;
            width: calc(100% - 20px);
          }
      `}
    }
  }
`;

const CardWrap = styled.div`
  @media ${device.tablet} {
    padding-top: 80px;
  }
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  position: relative;
  padding: 35px 0;
  width: 100%;

  text-align: left;

  background-color: transparent;
  appearance: none;
  cursor: pointer;

  @media ${device.tablet} {
    padding: 45px 0;
  }
`;

const Heading = styled.h3`
  margin-left: 24px;
  font-weight: bold;
`;

const Content = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ isFullWidth?: boolean }>`
  height: 0px;
  overflow: hidden;
`;

const AccordionMarkdown = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})`
  position: relative;
  padding: 40px 0 20px;
  width: 100%;
  opacity: 0;
`;

export const QuoteExpand = ({
  title,
  copy,
  expandableCopy,
  topSplitter,
  cards,
  onMouseEnter,
  onMouseLeave,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });
  const [hover, setHover] = useState(false);

  const setHeight = useSpring({
    height: isOpen ? `${get(copySize, 'height', 0) as number}px` : '0px',
    visability: isOpen ? 'visible' : 'hidden',
  });

  const fadeIn = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(10px)',
  });
  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  const { r } = useResource();

  return (
    <AuthorWrapper
      topSplitter={topSplitter}
      left={
        <>
          <Title>{title}</Title>
          <Copy>{copy}</Copy>

          <Content
            className={isOpen ? 'open' : ''}
            style={
              { ...setHeight, visibility: isOpen ? 'visible' : 'hidden' } as any
            }
            id="content"
          >
            <AccordionMarkdown ref={copyRef} style={{ ...fadeIn } as any}>
              <Expandable>{expandableCopy}</Expandable>
            </AccordionMarkdown>
          </Content>
          <ExpandButton
            onClick={() => setIsOpen(!isOpen)}
            role="button"
            aria-expanded={isOpen ? true : false}
            aria-controls="content"
            onMouseLeave={onLeave}
            onMouseEnter={onEnter}
          >
            <ButtonPlus isOpen={isOpen} isHover={hover} />
            <Heading>{isOpen ? r(res.readLess) : r(res.readMore)}</Heading>
          </ExpandButton>
        </>
      }
      right={<CardWrap>{cards}</CardWrap>}
      isArticle={false}
      align="Right"
    />
  );
};
